import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsCard } from "@/components/ui/settings-layout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/i18n";

import { defaultPortFor, portAfterSecurityChange, type SmtpSecurity } from "./smtpDefaults";

type MailSettings = components["schemas"]["MailSettingsDto"];

const SETTINGS_KEY = ["admin", "mail", "settings"] as const;

/** The order the Security select offers the modes in. */
const SECURITY_MODES: SmtpSecurity[] = ["Automatic", "None", "StartTls", "ImplicitTls"];

/**
 * Mail is the one part of Bugler that fails silently, so this card carries both halves of the
 * answer: where mail leaves through (editable, stored on the server from the first save — until
 * then the deployment's configuration applies), and the test button that asks the relay outright.
 */
export function MailSettingsCard() {
  const t = useT();
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/admin/mail/settings", {});
      if (error !== undefined || data === undefined) {
        throw new Error("Failed to load the mail settings.");
      }
      return data;
    },
    // While an admin is typing here, a focus refetch must not clobber the form.
    refetchOnWindowFocus: false,
  });

  return (
    <SettingsCard caption={t.server.mail.caption}>
      {settings.isPending && (
        <p className="text-[12.5px] text-[#8CA1B8]">{t.server.mail.loading}</p>
      )}
      {settings.isError && (
        <p className="text-[12.5px] text-[#F0685A]">{t.server.mail.loadFailed}</p>
      )}

      {settings.data !== undefined && (
        // Remounts whenever the server's answer changes (a save, a reset), which re-seeds the
        // fields and empties the password box; mid-edit refetches never fire (see above).
        <SettingsForm key={formSeed(settings.data)} initial={settings.data} />
      )}
    </SettingsCard>
  );
}

/** Everything the form seeds from — a changed seed means the stored truth moved under us. */
function formSeed(settings: MailSettings): string {
  return [
    settings.source,
    settings.host,
    settings.port,
    settings.security,
    settings.username,
    settings.hasPassword,
    settings.from,
  ].join(" ");
}

type PasswordEdit = { kind: "keep" } | { kind: "set"; value: string } | { kind: "clear" };

function SettingsForm(props: { initial: MailSettings }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [host, setHost] = useState(props.initial.host);
  const [port, setPort] = useState(String(props.initial.port));
  const [security, setSecurity] = useState<SmtpSecurity>(props.initial.security);
  const [username, setUsername] = useState(props.initial.username);
  const [password, setPassword] = useState<PasswordEdit>({ kind: "keep" });
  const [from, setFrom] = useState(props.initial.from);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.PUT("/api/admin/mail/settings", {
        body: {
          host,
          port: Number(port),
          security,
          username,
          // Write-only: null keeps what is stored, an empty string removes it.
          password:
            password.kind === "keep" ? null : password.kind === "clear" ? "" : password.value,
          from,
        },
      });
      if (error !== undefined || data === undefined) {
        throw new Error(problemDetail(error) ?? t.server.mail.saveFallback);
      }
      return data;
    },
    onSuccess: data => queryClient.setQueryData(SETTINGS_KEY, data),
  });

  const reset = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE("/api/admin/mail/settings", {});
      if (error !== undefined || data === undefined) {
        throw new Error(problemDetail(error) ?? t.server.mail.resetFailed);
      }
      return data;
    },
    onSuccess: data => queryClient.setQueryData(SETTINGS_KEY, data),
  });

  const portValid = /^\d+$/.test(port) && Number(port) >= 1 && Number(port) <= 65535;
  const canSave = host.trim().length > 0 && from.trim().length > 0 && portValid;
  const dirty =
    host !== props.initial.host ||
    port !== String(props.initial.port) ||
    security !== props.initial.security ||
    username !== props.initial.username ||
    password.kind !== "keep" ||
    from !== props.initial.from;

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-[62ch] text-[12.5px] text-[#8CA1B8]">{t.server.mail.intro}</p>

      <div className="flex gap-3">
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="smtp-host">{t.server.mail.hostLabel}</Label>
          <Input
            id="smtp-host"
            value={host}
            placeholder={t.server.mail.hostPlaceholder}
            onChange={event => setHost(event.currentTarget.value)}
          />
        </div>
        <div className="grid w-[110px] gap-1.5">
          <Label htmlFor="smtp-port">{t.server.mail.portLabel}</Label>
          <Input
            id="smtp-port"
            inputMode="numeric"
            value={port}
            aria-invalid={!portValid}
            onChange={event => setPort(event.currentTarget.value)}
          />
        </div>
      </div>

      {port !== String(defaultPortFor(security)) && (
        // A port off the mode's convention is left alone (it may be deliberate — see
        // portAfterSecurityChange), so the convention is offered here instead, one click away.
        <button
          type="button"
          className="w-fit text-[11.5px] text-[#7D93AA] underline decoration-dotted underline-offset-2 hover:text-foreground"
          onClick={() => setPort(String(defaultPortFor(security)))}
        >
          {t.server.mail.usualPort(defaultPortFor(security))}
        </button>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="smtp-security">{t.server.mail.securityLabel}</Label>
        <Select
          value={security}
          onValueChange={value => {
            const next = value as SmtpSecurity;
            // The port follows the mode's convention only while it still stands on one.
            if (portValid) {
              setPort(String(portAfterSecurityChange(Number(port), security, next)));
            }
            setSecurity(next);
          }}
        >
          <SelectTrigger id="smtp-security" className="w-[340px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECURITY_MODES.map(mode => (
              <SelectItem key={mode} value={mode}>
                {t.server.mail.security[mode]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3">
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="smtp-username">{t.server.mail.usernameLabel}</Label>
          <Input
            id="smtp-username"
            autoComplete="off"
            value={username}
            onChange={event => setUsername(event.currentTarget.value)}
          />
        </div>
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="smtp-password">{t.server.mail.passwordLabel}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="smtp-password"
              type="password"
              autoComplete="new-password"
              className="flex-1"
              value={password.kind === "set" ? password.value : ""}
              placeholder={
                password.kind === "clear"
                  ? t.server.mail.passwordRemovedOnSave
                  : props.initial.hasPassword
                    ? t.server.mail.passwordSavedKeep
                    : ""
              }
              disabled={password.kind === "clear"}
              onChange={event =>
                // Typing replaces the stored password; erasing it all backs out of the edit.
                // Removing the stored password is the explicit button, never an empty box.
                setPassword(
                  event.currentTarget.value.length === 0
                    ? { kind: "keep" }
                    : { kind: "set", value: event.currentTarget.value },
                )
              }
            />
            {props.initial.hasPassword && password.kind === "keep" && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setPassword({ kind: "clear" })}
              >
                {t.server.mail.removeButton}
              </Button>
            )}
            {password.kind === "clear" && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setPassword({ kind: "keep" })}
              >
                {t.server.mail.keepButton}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="smtp-from">{t.server.mail.fromLabel}</Label>
        <Input
          id="smtp-from"
          className="w-[340px]"
          value={from}
          placeholder="bugler@example.com"
          onChange={event => setFrom(event.currentTarget.value)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          disabled={!canSave || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t.server.mail.savingButton : t.server.mail.saveButton}
        </Button>

        {props.initial.source === "Stored" ? (
          <>
            <span className="text-[12.5px] text-[#8CA1B8]">{t.server.mail.storedNote}</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={reset.isPending}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? t.server.mail.resettingButton : t.server.mail.resetButton}
            </Button>
          </>
        ) : (
          <span className="text-[12.5px] text-[#8CA1B8]">{t.server.mail.fromConfigurationNote}</span>
        )}
      </div>

      {save.isError && <ErrorNote title={t.server.mail.saveFailedTitle} error={save.error} />}
      {reset.isError && <ErrorNote title={t.server.mail.resetFailed} error={reset.error} />}

      <TestSection dirty={dirty} />
    </div>
  );
}

/**
 * Proves the saved configuration out loud, and reports whatever the mail server actually said.
 * Always the saved one: an unsaved form is exactly what an alert would not use either.
 */
function TestSection(props: { dirty: boolean }) {
  const t = useT();
  const send = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/api/admin/mail/test", {});
      if (error !== undefined) {
        throw new Error(problemDetail(error) ?? t.server.mail.sendRefused);
      }
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-3 border-t border-[#1E344C] pt-4">
      <p className="max-w-[62ch] text-[12.5px] text-[#8CA1B8]">{t.server.mail.testIntro}</p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={send.isPending}
          onClick={() => send.mutate()}
        >
          {send.isPending ? t.server.mail.sendingButton : t.server.mail.sendTestButton}
        </Button>

        {props.dirty && (
          <span className="text-[12.5px] text-[#8CA1B8]">{t.server.mail.testsSavedNote}</span>
        )}

        {send.isSuccess && send.data !== undefined && (
          <span className="text-[12.5px] text-[#8CA1B8]">
            {t.server.mail.sentToPrefix}{" "}
            <code className="font-mono text-[12px]">{send.data.sentTo}</code>.
          </span>
        )}
      </div>

      {send.isError && <ErrorNote title={t.server.mail.sendFailedTitle} error={send.error} />}
    </div>
  );
}

function ErrorNote(props: { title: string; error: Error }) {
  return (
    <div className="flex flex-col gap-1 rounded-[9px] border border-[rgba(229,84,74,0.45)] bg-[rgba(229,84,74,0.10)] p-3">
      <p className="text-[12.5px] font-semibold text-[#F0685A]">{props.title}</p>
      {/* The refusal itself: an operator setting this up would otherwise go to the container log for it. */}
      <code className="font-mono text-[11.5px] break-words text-[#F6C7C2]">
        {props.error.message}
      </code>
    </div>
  );
}

/** ProblemDetails carries what actually went wrong; anything else is not worth guessing at. */
function problemDetail(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const problem = error as { detail?: unknown; title?: unknown };
  if (typeof problem.detail === "string") {
    return problem.detail;
  }

  return typeof problem.title === "string" ? problem.title : undefined;
}
